import React from 'react';
import { useDispatch } from 'react-redux';
import { FormattedDate } from 'react-intl';
import { Grid, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { GridListItem, type ItemLayoutProps } from 'app/main/components/GridList';
import { Icon, ImageAvatar, showDeleteDialog, SquareChip, Text, useSettings, Utils } from 'app/main/components';

export default function GroupsUsersListItem(props: ItemLayoutProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const theme = useTheme();

	function setManager() {
		Utils.executeStored({
			action: 'st_groups_users_update',
			dispatch,
			data: {
				grus_id: props.model.grus_id,
				grus_is_manager: true
			},
			successHandler: response => {
				props.buttonProps.updateRefresh();
			}
		});
	}

	function remove() {
		dispatch(
			showDeleteDialog({
				module: 'groups_users',
				id: props.model.grus_id,
				title: props.model.user_title,
				hardDelete: true,
				replace: false,
				updateRefresh: props.buttonProps.updateRefresh
			})
		);
	}

	const avatar = (
		<ImageAvatar
			dimensions={44}
			module="users"
			uid={props.model.grus_user_uid}
			avatar={props.model.user_avatar}
			initials={props.model.user_title_initials}
			labelBadge={props.model.grus_is_manager ? 'M' : undefined}
			labelBadgeTooltip={props.model.grus_is_manager ? t('GROUPS_USERS_IS_MANAGER') : undefined}
			badgeColor={props.model.grus_is_manager ? theme.palette.primary.main : undefined}
		/>
	);

	const secondaryAction = (
		<>
			<Tooltip title={t('VIEW_ITEM_BUTTON_GROUP_SET_MANAGER')} arrow>
				<IconButton onClick={setManager}>
					<Icon icon="account-tie" />
				</IconButton>
			</Tooltip>
			<Tooltip title={t('VIEW_ITEM_BUTTON_REMOVE')} arrow>
				<IconButton onClick={remove} edge="end">
					<Icon icon="account-remove" />
				</IconButton>
			</Tooltip>
		</>
	);

	return (
		<GridListItem avatar={avatar} secondaryAction={secondaryAction}>
			<Grid container justifyContent="space-between" alignItems="center" spacing={1}>
				<Grid size={props.width >= 720 ? 6 : 12}>
					<Grid container>
						<Grid size={12} className="inline-flex items-center">
							{/* WARNING */}
							{props.model.user_warning_desc && (
								<Tooltip title={props.model.user_warning_desc} arrow>
									<Icon className="mr-2" icon="alert-circle-outline" color="warning.main" />
								</Tooltip>
							)}
							{/* BLOCKED */}
							{props.model.user_blocked_desc && (
								<Tooltip title={props.model.user_blocked_desc} arrow>
									<Icon className="mr-2" icon="block" color="error.main" />
								</Tooltip>
							)}
							{/* TITLE */}
							<Text
								className="font-600"
								variant="subtitle2"
								to={{ module: 'users', uid: props.model.grus_user_uid }}
							>
								{props.model.user_title}
							</Text>
						</Grid>
						<Grid>
							{/* ROLES */}
							{props.model.user_roles?.map(role => (
								<SquareChip
									key={role.usra_id}
									label={role.usrl_title}
									backgroundColor={role.usrl_color}
								/>
							))}
						</Grid>
					</Grid>
				</Grid>
				<Grid size={props.width >= 720 ? 6 : 12}>
					<Text className="font-400" variant="subtitle2" color="text.secondary">
						{props.model.grus_d_created ? (
							<FormattedDate
								value={Utils.parseDate(props.model.grus_d_created)?.toDate()}
								year="numeric"
								month="long"
								day="numeric"
								hour="numeric"
								minute="numeric"
							/>
						) : (
							t('VIEW_ITEM_NO_DATES')
						)}
					</Text>
				</Grid>
			</Grid>
		</GridListItem>
	);
}
